import { useState, useEffect, useRef, useMemo, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

// Pages laid out as wide boards: no max width, slimmer side padding.
const WIDE_PAGES = ["/tech-tickets"];
// Charts: half the usual side margin (the 1400px cap's centring plus the padding).
const HALF_MARGIN_PAGES = ["/charts"];
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { useAppStore } from "@/store/auth";
import { NotificationBell } from "@/components/ui/NotificationBell";
import { getCurrentUser, getRealUser } from "@/lib/session";
import { DeployStatusPill } from "@/components/layout/DeployStatusPill";
import { PERM, hasPermission, hasAnyPermission, hasBankPaymentsAccess } from "@/lib/permissions";
import { api } from "@/lib/api";
import { clientFxApi } from "@/lib/clientFxApi";
import { applyLayout, isGroup, layoutOf, navLayoutApi, type NavEntry, type NavGroup, type NavItem, type NavLayout } from "@/lib/navLayout";
import NavCustomiseModal from "@/components/layout/NavCustomiseModal";
import {
  IconDashboard, IconWallet, IconTrade, IconTarget, IconTransaction,
  IconStatement, IconProfile, IconSearch,
} from "@/components/ui";

function IconPercent({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M19 5L5 19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" /></svg>;
}

function IconAdmin({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>;
}
function IconInvite({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" /></svg>;
}
function IconSettings({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /></svg>;
}
function IconBeneficiary({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" /></svg>;
}
function IconThirdParty({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>;
}
function IconCalendar2({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>;
}
function IconChevron({ className, size = 16, open = false }: { className?: string; size?: number; open?: boolean }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cn("transition-transform duration-200", open && "rotate-90", className)}><polyline points="9 18 15 12 9 6" /></svg>;
}
function IconPortfolio({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" /><line x1="12" y1="12" x2="12" y2="12.01" /></svg>;
}
function IconCounterparties({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></svg>;
}
function IconCommission({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>;
}
function IconMessages({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>;
}
function IconMfx({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /></svg>;
}
function IconMarket({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>;
}
function IconChart({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>;
}
function IconArticle({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>;
}
function IconCalendarEcon({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>;
}
function IconReferral({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M20 12v10H4V12" /><path d="M2 7h20v5H2z" /><path d="M12 22V7" /><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z" /></svg>;
}
function IconMassPayments({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><rect x="3" y="4" width="18" height="16" rx="2" /><line x1="7" y1="9" x2="17" y2="9" /><line x1="7" y1="13" x2="17" y2="13" /><line x1="7" y1="17" x2="13" y2="17" /></svg>;
}
function IconUpload({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>;
}
function IconWorkbench({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>;
}
function IconHistory({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3 12a9 9 0 109-9 9.75 9.75 0 00-6.74 2.74L3 8" /><polyline points="3 3 3 8 8 8" /><polyline points="12 7 12 12 15 14" /></svg>;
}
function IconFiles({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z" /><polyline points="13 2 13 9 20 9" /></svg>;
}
function IconLending({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3 10h18M6 14h4" /><rect x="2" y="5" width="20" height="14" rx="2" /></svg>;
}

function IconBankPayments({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3 10h18M5 10V7l7-4 7 4v3M6 10v8M10 10v8M14 10v8M18 10v8M3 21h18" /></svg>;
}
function IconLeads({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3 4h18l-7 8.5V19l-4 2v-8.5L3 4z" /></svg>;
}
function IconSettlements({ className, size = 20 }: { className?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M3 9.4V7.6A1.6 1.6 0 0 1 4.6 6h14.8A1.6 1.6 0 0 1 21 7.6v1.8a2.6 2.6 0 0 0 0 5.2v1.8a1.6 1.6 0 0 1-1.6 1.6H4.6A1.6 1.6 0 0 1 3 16.4v-1.8a2.6 2.6 0 0 0 0-5.2Z" />
      <path d="M14.5 6.6v10.8" strokeDasharray="2 2" />
    </svg>
  );
}

/** Outgoing payment: a banknote with an arrow heading out to the right. */
function IconPaymentOut({ className, size = 20 }: { className?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round" className={className}>
      <rect x="2" y="6.5" width="13" height="11" rx="2" />
      <circle cx="8.5" cy="12" r="2" />
      <path d="M15 12h7" />
      <path d="m19 9 3 3-3 3" />
    </svg>
  );
}

/** Compliance Queue: a shield with a tick. */
function IconComplianceQueue({ className, size = 20 }: { className?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.3 7.5 9.5 4.4-1.2 7.5-4.9 7.5-9.5V6Z" />
      <path d="m8.8 12.2 2.2 2.2 4.3-4.6" />
    </svg>
  );
}

/** Tech Tickets: a ticket stub. */
function IconTechTickets({ className, size = 20 }: { className?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M3 9a2 2 0 0 0 0 6v3a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1v-3a2 2 0 0 0 0-6V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1Z" />
      <path d="M9 9h6M9 12h6M9 15h3" />
    </svg>
  );
}

function IconApplications({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="m9 14 2 2 4-4" /></svg>;
}
function IconClients({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>;
}
function IconHub({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="3" /><path d="M12 2v4M12 18v4M2 12h4M18 12h4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M4.9 19.1l2.8-2.8M16.3 7.7l2.8-2.8" /></svg>;
}

function IconFolder({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></svg>;
}
function IconSliders({ className, size = 20 }: { className?: string; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={className}><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></svg>;
}

// ── Nav structure ─────────────────────────────
// Types and the staff's own layout live in lib/navLayout.ts.

const PORTFOLIO_GROUP: NavGroup = {
  label: "Portfolio",
  icon: IconPortfolio,
  children: [
    { to: "/accounts", icon: IconWallet, label: "Accounts" },
    { to: "/trades", icon: IconTrade, label: "Trades" },
    { to: "/transactions", icon: IconTransaction, label: "Transactions" },
  ],
};

const COUNTERPARTIES_GROUP: NavGroup = {
  label: "Counterparties",
  icon: IconCounterparties,
  children: [
    { to: "/beneficiaries", icon: IconBeneficiary, label: "Beneficiaries" },
    { to: "/third-parties", icon: IconThirdParty, label: "Third Parties" },
  ],
};

const MARKET_GROUP: NavGroup = {
  label: "Market Intelligence",
  icon: IconMarket,
  children: [
    { to: "/charts", icon: IconChart, label: "Charts" },
    { to: "/articles", icon: IconArticle, label: "Articles" },
    { to: "/calendar", icon: IconCalendarEcon, label: "Economic Calendar" },
  ],
};

const BASE_NAV: NavEntry[] = [
  { to: "/", icon: IconDashboard, label: "Dashboard" },
  PORTFOLIO_GROUP,
  COUNTERPARTIES_GROUP,
  MARKET_GROUP,
  { to: "/messages", icon: IconMessages, label: "Messages" },
  { to: "/statements", icon: IconStatement, label: "Statements" },
  { to: "/lending", icon: IconLending, label: "Lending" },
  { to: "/referrals", icon: IconReferral, label: "Referrals" },
  { to: "/profile", icon: IconProfile, label: "Profile" },
  { to: "/settings", icon: IconSettings, label: "Settings" },
];

type NavState = {
  entries: NavEntry[];
  /** Hub (staff, no client open): the menu they can customise. */
  hub: null | { defaults: NavEntry[]; layout: NavLayout | null; save: (l: NavLayout) => Promise<void>; reset: () => Promise<void> };
};

/** The menu, with the staff member's own layout applied on the Hub. */
function useNavEntries(): NavState {
  const standard = useStandardNav();
  const user = getCurrentUser();
  const accountId = useAppStore((s) => s.accountId);
  const role = user?.role;
  const hubMode = (role === "mfx_admin" || role === "mfx_pm" || role === "mfx_compliance"
    || role === "mfx_settlements" || user?.isSuperAdmin === true) && !accountId;
  const queryClient = useQueryClient();
  const key = ["nav-layout", user?.id];
  const { data } = useQuery({ queryKey: key, queryFn: navLayoutApi.get, enabled: hubMode, staleTime: 10 * 60_000 });
  const layout = hubMode ? data?.layout ?? null : null;
  const entries = useMemo(() => applyLayout(standard, layout, IconFolder), [standard, layout]);
  if (!hubMode) return { entries: standard, hub: null };
  return {
    entries,
    hub: {
      defaults: standard,
      layout,
      save: async (l) => { queryClient.setQueryData(key, await navLayoutApi.save(l)); },
      reset: async () => { queryClient.setQueryData(key, await navLayoutApi.reset()); },
    },
  };
}

function useStandardNav(): NavEntry[] {
  const user = getCurrentUser();
  const accountId = useAppStore((s) => s.accountId);
  const role = user?.role;

  // Mass Payments access lookup — staff are unconditional, clients depend on their account's Vertical.
  // Cached aggressively because the answer rarely changes within a session.
  const { data: massPaymentsAccess } = useQuery({
    queryKey: ["mass-payments-access", accountId, role],
    queryFn: () => api.getMassPaymentsAccess(accountId!),
    enabled: !!accountId,
    staleTime: 5 * 60_000,
  });

  // Convert (client FX) is client-facing: only the client's own users (and
  // staff in View As, who appear as the client), and only for accounts it's
  // switched on for. Staff looking at a client don't get it.
  const clientUser = role === "client" || role === "client_sub";
  const { data: clientFx } = useQuery({
    queryKey: ["client-fx", accountId],
    queryFn: () => clientFxApi.overview(accountId!),
    enabled: !!accountId && clientUser,
    staleTime: 5 * 60_000,
  });

  // Which client-facing pages are switched ON. Internal staff get the full
  // set back (the toggle never hides their tools); clients get only ON pages.
  const { data: clientFacing } = useQuery({
    queryKey: ["client-facing-pages", role],
    queryFn: () => api.getMyClientFacingPages(),
    staleTime: 5 * 60_000,
  });

  return useMemo(() => {
    const isSuperAdmin = user?.isSuperAdmin === true;
    const isInternal = role === "mfx_admin" || role === "mfx_pm"
      || role === "mfx_compliance" || role === "mfx_settlements" || isSuperAdmin;

    // ── Hub mode ─────────────────────────────────────────────
    // Staff with no client selected get the staff nav. Client-scoped
    // groups (Portfolio, Counterparties) are absent because there's no
    // client; tools are grouped under a heading named for the profile.
    if (isInternal && !accountId) {
      // Mass Payments stays client-scoped for now — it's reached by
      // opening a client, not from the Hub.
      const tools: NavItem[] = [];
      if (hasPermission(user, PERM.RISK)) tools.push({ to: "/risk", icon: IconChart, label: "Risk Management" });
      if (hasPermission(user, PERM.OPS_TOOLS)) tools.push({ to: "/operations-tools", icon: IconMfx, label: "Operations Tools" });
      if (hasAnyPermission(user, PERM.KPIS_ALL, PERM.KPIS_SCOPED, PERM.KPIS_COMPLIANCE_ALL, PERM.KPIS_COMPLIANCE_OWN)) tools.push({ to: "/kpis", icon: IconMfx, label: "KPIs" });
      if (hasPermission(user, PERM.REPORTING)) tools.push({ to: "/reporting", icon: IconChart, label: "Reporting" });
      if (hasPermission(user, PERM.COMMISSIONS)) tools.push({ to: "/commissions", icon: IconCommission, label: "Commissions" });
      if (hasPermission(user, PERM.INVITE_CLIENT)) tools.push({ to: "/invite", icon: IconInvite, label: "Invite Client" });
      if (hasPermission(user, PERM.COMPLIANCE_TOOLS)) tools.push({ to: "/compliance-tools", icon: IconMfx, label: "Compliance Tools" });
      if (hasPermission(user, PERM.ENTITY_SCREENING)) tools.push({ to: "/entity-screening", icon: IconSearch, label: "Entity Screening" });
      if (hasPermission(user, PERM.ADJUDICATION)) tools.push({ to: "/auto-adjudication", icon: IconSearch, label: "Auto-Adjudication" });
      if (hasPermission(user, PERM.DISPLAY_SCREENS)) tools.push({ to: "/display/screens", icon: IconChart, label: "Display screens" });
      if (role === "mfx_admin" || role === "mfx_compliance" || role === "mfx_pm" || isSuperAdmin) {
        tools.push({ to: "/client-documents", icon: IconMfx, label: "Client Documents" });
      }
      if (hasPermission(user, PERM.CONTROL_CENTRE)) tools.push({ to: "/control-centre", icon: IconMfx, label: "Control Centre" });
      if (hasPermission(user, PERM.NOTIFICATIONS)) tools.push({ to: "/admin/notifications", icon: IconMfx, label: "Notifications" });
      if (role === "mfx_admin") {
        tools.push({ to: "/admin", icon: IconAdmin, label: "Admin" });
        tools.push({ to: "/settings/cutoffs", icon: IconSettings, label: "Cut-off times" });
        tools.push({ to: "/admin/scheduled-emails", icon: IconSettings, label: "Scheduled emails" });
      }
      if (isSuperAdmin) {
        tools.push({ to: "/super-admin", icon: IconMfx, label: "Super Admin" });
        tools.push({ to: "/api-usage", icon: IconChart, label: "API Usage" });
        tools.push({ to: "/client-facing", icon: IconMfx, label: "Client-Facing Pages" });
      }
      const toolsLabel =
        role === "mfx_settlements" ? "Operations"
        : role === "mfx_pm" ? "Trading"
        : role === "mfx_compliance" ? "Compliance"
        : "Admin";
      const hub: NavEntry[] = [
        { to: "/", icon: IconDashboard, label: "Dashboard" },
        { to: "/clients", icon: IconClients, label: "Clients" },
      ];
      // Trade booking is daily work for dealers and ops, so it sits at
      // the top level next to Clients, not inside the tools group.
      if (role === "mfx_pm" || role === "mfx_settlements" || role === "mfx_admin" || isSuperAdmin) {
        hub.push({ to: "/trade-booking", icon: IconTrade, label: "Trades" });
      }
      // Auto-Fees: landing fees booked automatically for chosen clients.
      if (role === "mfx_pm" || role === "mfx_settlements" || role === "mfx_admin" || isSuperAdmin) {
        hub.push({ to: "/auto-fees", icon: IconPercent, label: "Auto-Fees" });
      }
      // Leads is daily work for PMs, so it sits at the top level directly
      // under Trades rather than inside the tools group.
      if (hasAnyPermission(user, PERM.LEADS_ALL, PERM.LEADS_SCOPED)) {
        hub.push({ to: "/leads", icon: IconLeads, label: "Leads" });
      }
      // Applications: onboarding pipeline and compliance queue. PMs chase
      // partials, Compliance decides, Admin sees everything.
      if (role === "mfx_admin" || role === "mfx_pm" || role === "mfx_compliance" || isSuperAdmin) {
        hub.push({ to: "/applications", icon: IconApplications, label: "Applications" });
      }
      if (hasBankPaymentsAccess(user)) {
        hub.push({ to: "/bank-payments", icon: IconBankPayments, label: "Bank Payments" });
      }
      // New payments: traders create client payments. Granted on the Permissions page.
      if (hasPermission(user, PERM.NEW_PAYMENTS)) {
        hub.push({ to: "/payments/new", icon: IconPaymentOut, label: "New payments" });
      }
      // Settlements Queue: tickets between trading and settlements.
      if (hasPermission(user, PERM.SETTLEMENTS_QUEUE)) {
        hub.push({ to: "/settlements", icon: IconSettlements, label: "Settlements Queue" });
      }
      // Pay Now: immediate iFAST payments, two-person approval.
      if (hasPermission(user, PERM.PAY_NOW)) {
        hub.push({ to: "/pay-now", icon: IconPaymentOut, label: "Pay Now" });
      }
      // Compliance Queue: requests between Compliance and the PMs.
      if (hasPermission(user, PERM.COMPLIANCE_QUEUE)) {
        hub.push({ to: "/compliance-queue", icon: IconComplianceQueue, label: "Compliance Queue" });
      }
      // Tech Tickets: every staff member can raise and follow tickets.
      hub.push({ to: "/tech-tickets", icon: IconTechTickets, label: "Tech Tickets" });
      // Dev Zone: super admin only, deliberately last and out of the way.
      if (user?.isSuperAdmin) {
        tools.push({ to: "/dev", icon: IconSettings, label: "Dev Zone" });
      }
      hub.push(
        MARKET_GROUP,
        { to: "/messages", icon: IconMessages, label: "Messages" },
        { to: "/statements", icon: IconStatement, label: "Statements" },
      );
      if (tools.length > 0) hub.push({ label: toolsLabel, icon: IconMfx, children: tools });
      hub.push(
        { to: "/profile", icon: IconProfile, label: "Profile" },
        { to: "/settings", icon: IconSettings, label: "Settings" },
      );
      return hub;
    }

    // Base (client-eligible) nav. For clients, keep only pages currently
    // switched client-facing; staff see the lot. Mass Payments + MFX are
    // added afterwards and gated by their own rules, so they're untouched.
    let base: NavEntry[] = [...BASE_NAV];
    if (!isInternal) {
      const allow = new Set(clientFacing?.paths ?? []);
      base = base
        .map((e) => {
          if (isGroup(e)) {
            const kids = e.children.filter((c) => allow.has(c.to));
            return kids.length ? { ...e, children: kids } : null;
          }
          return allow.has(e.to) ? e : null;
        })
        .filter((e): e is NavEntry => e !== null);
    }
    const entries: NavEntry[] = [...base];

    // Convert: its own top-level item, after Portfolio, when the account has it.
    if (clientUser && clientFx?.enabled) {
      const convert: NavItem = { to: "/convert", icon: IconTrade, label: "Convert" };
      const pIdx = entries.findIndex((e) => isGroup(e) && e.label === "Portfolio");
      entries.splice(pIdx >= 0 ? pIdx + 1 : 1, 0, convert);
      // Advanced options: their limit orders, straight after Convert.
      if (clientFx.advanced) {
        const at = entries.indexOf(convert);
        entries.splice(at + 1, 0, { to: "/limit-orders", icon: IconTarget, label: "Limit Orders" });
      }
    }

    // Mass Payments group — inserted right after Market Intelligence
    // so it sits in the operational/portfolio cluster, not down with
    // profile/settings.
    if (massPaymentsAccess?.hasAccess) {
      const massPaymentsChildren: NavItem[] = [
        { to: "/mass-payments/upload", icon: IconUpload, label: "Upload" },
        { to: "/mass-payments/workbench", icon: IconWorkbench, label: "Pending" },
      ];
      if (massPaymentsAccess.canSeeFiles) {
        massPaymentsChildren.push({ to: "/mass-payments/files", icon: IconFiles, label: "Files" });
        massPaymentsChildren.push({ to: "/mass-payments/orphans", icon: IconFiles, label: "Orphan Payments" });
      }
      const massPaymentsGroup: NavGroup = {
        label: "Mass Payments",
        icon: IconMassPayments,
        children: massPaymentsChildren,
      };
      const marketIdx = entries.findIndex(
        (e) => "label" in e && e.label === "Market Intelligence",
      );
      if (marketIdx >= 0) {
        entries.splice(marketIdx + 1, 0, massPaymentsGroup);
      } else {
        entries.push(massPaymentsGroup);
      }
    }

    // MFX Options accordion — driven by the permissions matrix. Each
    // child is gated independently; the group renders if any are visible.
    const mfxChildren: NavItem[] = [];
    if (hasPermission(user, PERM.COMMISSIONS)) {
      mfxChildren.push({ to: "/commissions", icon: IconCommission, label: "Commissions" });
    }
    if (hasPermission(user, PERM.INVITE_CLIENT)) {
      mfxChildren.push({ to: "/invite", icon: IconInvite, label: "Invite Client" });
    }
    // Admin page stays role-gated (mfx_admin only) — managing users
    // is outside the matrix.
    if (role === "mfx_admin") {
      mfxChildren.push({ to: "/admin", icon: IconAdmin, label: "Admin" });
      mfxChildren.push({ to: "/admin/scheduled-emails", icon: IconSettings, label: "Scheduled emails" });
    }
    if (hasAnyPermission(user, PERM.KPIS_ALL, PERM.KPIS_SCOPED)) {
      mfxChildren.push({ to: "/kpis", icon: IconMfx, label: "KPIs" });
    }
    if (hasPermission(user, PERM.OPS_TOOLS)) {
      mfxChildren.push({ to: "/operations-tools", icon: IconMfx, label: "Operations Tools" });
    }
    if (hasPermission(user, PERM.COMPLIANCE_TOOLS)) {
      mfxChildren.push({ to: "/compliance-tools", icon: IconMfx, label: "Compliance Tools" });
    }
    if (hasPermission(user, PERM.ENTITY_SCREENING)) {
      mfxChildren.push({ to: "/entity-screening", icon: IconSearch, label: "Entity Screening" });
    }
    if (hasPermission(user, PERM.ADJUDICATION)) {
      mfxChildren.push({ to: "/auto-adjudication", icon: IconSearch, label: "Auto-Adjudication" });
    }
    if (hasPermission(user, PERM.RISK)) {
      mfxChildren.push({ to: "/risk", icon: IconChart, label: "Risk Management" });
    }
    if (hasPermission(user, PERM.REPORTING)) {
      mfxChildren.push({ to: "/reporting", icon: IconChart, label: "Reporting" });
    }
    if (hasAnyPermission(user, PERM.LEADS_ALL, PERM.LEADS_SCOPED)) {
      mfxChildren.push({ to: "/leads", icon: IconMfx, label: "Leads" });
    }
    if (hasPermission(user, PERM.CONTROL_CENTRE)) {
      mfxChildren.push({ to: "/control-centre", icon: IconMfx, label: "Control Centre" });
    }
    if (hasPermission(user, PERM.NOTIFICATIONS)) {
      mfxChildren.push({ to: "/admin/notifications", icon: IconMfx, label: "Notifications" });
    }
    // Client Documents — Compliance, admins and account managers.
    if (role === "mfx_admin" || role === "mfx_compliance" || role === "mfx_pm" || isSuperAdmin) {
      mfxChildren.push({ to: "/client-documents", icon: IconMfx, label: "Client Documents" });
    }
    // Super Admin stays super-admin-only; not in the matrix.
    if (isSuperAdmin) {
      mfxChildren.push({ to: "/super-admin", icon: IconMfx, label: "Super Admin" });
      mfxChildren.push({ to: "/api-usage", icon: IconChart, label: "API Usage" });
      mfxChildren.push({ to: "/client-facing", icon: IconMfx, label: "Client-Facing Pages" });
    }
    if (mfxChildren.length > 0) {
      entries.push({
        label: "MFX Options",
        icon: IconMfx,
        children: mfxChildren,
      });
    }
    return entries;
  }, [user, role, accountId, massPaymentsAccess?.hasAccess, massPaymentsAccess?.canSeeFiles, clientFacing?.paths, clientFx]);
}

function useIsInternal(): boolean {
  const user = getCurrentUser();
  const role = user?.role || "";
  return role === "mfx_admin" || role === "mfx_pm" || role === "mfx_compliance" || role === "mfx_settlements";
}

// ── Collapsible Nav Group ─────────────────────

function NavGroupItem({ group, collapsed: sidebarCollapsed, onExpandSidebar }: { group: NavGroup; collapsed: boolean; onExpandSidebar: () => void }) {
  const location = useLocation();
  const isChildActive = group.children.some(
    (c) => c.to === "/" ? location.pathname === "/" : location.pathname.startsWith(c.to)
  );
  const [open, setOpen] = useState(isChildActive);

  const handleClick = () => {
    if (sidebarCollapsed) {
      // Expand the sidebar and open the accordion
      onExpandSidebar();
      setOpen(true);
    } else {
      setOpen(!open);
    }
  };

  return (
    <div>
      <button
        onClick={handleClick}
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-xl text-body-sm font-medium transition-all duration-150 w-full",
          isChildActive
            ? "text-brand-600 dark:text-accent-300"
            : "text-dark-400 hover:bg-surface-secondary dark:hover:bg-dark-600 hover:text-dark-600 dark:hover:text-dark-100"
        )}
        title={sidebarCollapsed ? group.label : undefined}
      >
        <group.icon size={20} className="shrink-0" />
        {!sidebarCollapsed && (
          <>
            <span className="flex-1 text-left">{group.label}</span>
            <IconChevron size={14} open={open} className="text-dark-300" />
          </>
        )}
      </button>
      {open && !sidebarCollapsed && (
        <div className="ml-5 pl-3 border-l border-surface-border/50 dark:border-dark-500/50 space-y-0.5 mt-0.5 mb-1">
          {group.children.map(({ to, icon: Icon, label }) => {
            const isActive = to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);
            return (
              <NavLink
                key={to}
                to={to}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-lg text-body-sm font-medium transition-all duration-150",
                  isActive
                    ? "bg-brand-50 dark:bg-dark-500 text-brand-600 dark:text-accent-300"
                    : "text-dark-400 hover:bg-surface-secondary dark:hover:bg-dark-600 hover:text-dark-600 dark:hover:text-dark-100"
                )}
              >
                <Icon size={16} className="shrink-0" />
                <span>{label}</span>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Sidebar ───────────────────────────────────

function AccountSwitcher({
  accountName,
  accounts,
  onSelect,
}: {
  accountName: string | null;
  accounts: Array<{ accountId: string; accountName: string }>;
  onSelect: (a: { accountId: string; accountName: string }) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const activeId = useAppStore((s) => s.accountId);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="relative mb-1" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-3 py-2 rounded-xl bg-brand-50 dark:bg-dark-500 hover:bg-brand-100 dark:hover:bg-dark-400 transition-colors"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-2xs text-dark-300 uppercase tracking-wider font-medium">Viewing as</div>
            <div className="text-body-sm font-medium text-dark-700 dark:text-dark-100 truncate">{accountName}</div>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cn("shrink-0 text-dark-400 transition-transform", open && "rotate-180")}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </button>
      {open && (
        <div className="absolute bottom-full left-0 right-0 mb-1 rounded-xl border border-surface-border dark:border-dark-500 bg-white dark:bg-dark-700 shadow-lg overflow-hidden z-50 max-h-64 overflow-y-auto">
          <div className="px-3 py-1.5 text-2xs uppercase tracking-wider text-dark-300 border-b border-surface-border dark:border-dark-500">
            Your companies
          </div>
          {accounts.map((a) => {
            const active = a.accountId === activeId;
            return (
              <button
                key={a.accountId}
                onClick={() => { onSelect(a); setOpen(false); }}
                className={cn(
                  "w-full text-left px-3 py-2 text-body-sm transition-colors flex items-center justify-between gap-2 hover:bg-surface-secondary dark:hover:bg-dark-600",
                  active ? "text-brand-600 font-medium" : "text-dark-700 dark:text-dark-100",
                )}
              >
                <span className="truncate">{a.accountName || a.accountId}</span>
                {active && <span className="text-brand-500 shrink-0">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Sidebar({ collapsed, onToggle, accountName, onSwitchAccount, onLogout, navEntries, onCustomise }: {
  collapsed: boolean; onToggle: () => void; accountName: string | null;
  onSwitchAccount: () => void; onLogout: () => void; navEntries: NavEntry[];
  /** Hub only: opens Customise menu. */
  onCustomise?: () => void;
}) {
  const location = useLocation();
  const isInternal = useIsInternal();
  // Multi-company clients (one SF Contact linked to several Accounts) get a
  // switcher listing ONLY their entitled accounts — never the staff all-
  // accounts selector. Single-account clients see the static label.
  const switcherUser = getCurrentUser();
  const switchableAccounts = (switcherUser?.accounts ?? []) as Array<{ accountId: string; accountName: string }>;
  const selectAccount = useAppStore((s) => s.selectAccount);
  const canSwitch = !isInternal && switchableAccounts.length >= 2;

  return (
    <aside className={cn("fixed left-0 top-0 h-full bg-white dark:bg-dark-800 border-r border-surface-border dark:border-dark-500 z-40 flex flex-col transition-all duration-300 ease-out", collapsed ? "w-[72px]" : "w-[240px]")}>
      <div className="h-16 flex items-center px-5 border-b border-surface-border dark:border-dark-500 shrink-0">
        <div className="flex items-center gap-3 overflow-hidden">
          <img src="/logo.png" alt="Millbank FX" className="w-8 h-8 rounded-xl shrink-0" />
          {!collapsed && <span className="font-display font-bold text-heading-sm text-dark-800 dark:text-white whitespace-nowrap">MILLBANK FX</span>}
        </div>
      </div>
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto sidebar-scroll">
        {navEntries.map((entry, i) => {
          if (isGroup(entry)) {
            return <NavGroupItem key={entry.id ?? entry.label} group={entry} collapsed={collapsed} onExpandSidebar={() => { if (collapsed) onToggle(); }} />;
          }
          const { to, icon: Icon, label } = entry;
          const isActive = to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);
          return (
            <NavLink key={to} to={to} className={cn("flex items-center gap-3 px-3 py-2.5 rounded-xl text-body-sm font-medium transition-all duration-150", isActive ? "bg-brand-50 dark:bg-dark-500 text-brand-600 dark:text-accent-300" : "text-dark-400 hover:bg-surface-secondary dark:hover:bg-dark-600 hover:text-dark-600 dark:hover:text-dark-100")} title={collapsed ? label : undefined}>
              <Icon size={20} className="shrink-0" />{!collapsed && <span>{label}</span>}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-surface-border dark:border-dark-500 space-y-1">
        {!collapsed && accountName && (
          canSwitch ? (
            <AccountSwitcher
              accountName={accountName}
              accounts={switchableAccounts}
              onSelect={(a) => selectAccount(a.accountId, a.accountName)}
            />
          ) : (
            <div className="px-3 py-2 rounded-xl bg-brand-50 dark:bg-dark-500 mb-1">
              <div className="text-2xs text-dark-300 uppercase tracking-wider font-medium">Viewing as</div>
              <div className="text-body-sm font-medium text-dark-700 dark:text-dark-100 truncate">{accountName}</div>
            </div>
          )
        )}
        {isInternal && accountName && (
          <button onClick={onSwitchAccount} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-body-sm font-medium text-brand-600 hover:bg-brand-50 dark:hover:bg-dark-600 w-full transition-colors" title={collapsed ? "Back to MFX Hub" : undefined}>
            <IconHub size={20} className="shrink-0" />{!collapsed && <span>Back to MFX Hub</span>}
          </button>
        )}
        {onCustomise && (
          <button onClick={onCustomise} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-body-sm font-medium text-dark-400 hover:bg-surface-secondary dark:hover:bg-dark-600 hover:text-dark-600 dark:hover:text-dark-100 w-full transition-colors" title={collapsed ? "Customise menu" : undefined}>
            <IconSliders size={20} className="shrink-0" />{!collapsed && <span>Customise menu</span>}
          </button>
        )}
        <button onClick={onLogout} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-body-sm font-medium text-dark-400 hover:bg-status-error-bg hover:text-status-error w-full transition-colors" title={collapsed ? "Log Out" : undefined}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
          {!collapsed && <span>Log Out</span>}
        </button>
        <button onClick={onToggle} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-body-sm font-medium text-dark-400 hover:bg-surface-secondary dark:hover:bg-dark-600 hover:text-dark-600 dark:hover:text-dark-100 w-full transition-colors">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={cn("shrink-0 transition-transform duration-300", collapsed && "rotate-180")}><polyline points="11 17 6 12 11 7" /><polyline points="18 17 13 12 18 7" /></svg>
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </aside>
  );
}

// ── Mobile ────────────────────────────────────

function MobileHeader({ onMenuOpen }: { onMenuOpen: () => void }) {
  return (
    <header className="fixed top-0 left-0 right-0 h-14 bg-white dark:bg-dark-800 border-b border-surface-border dark:border-dark-500 z-50 flex items-center justify-between px-4 lg:hidden">
      <div className="flex items-center gap-3">
        <button onClick={onMenuOpen} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-surface-secondary dark:hover:bg-dark-600 transition-colors">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
        </button>
        <img src="/logo.png" alt="Millbank FX" className="w-7 h-7 rounded-lg" />
        <span className="font-display font-bold text-heading-sm text-dark-800 dark:text-white">MILLBANK FX</span>
      </div>
    </header>
  );
}

function MobileOverlay({ open, onClose, navEntries }: { open: boolean; onClose: () => void; navEntries: NavEntry[] }) {
  const location = useLocation();
  if (!open) return null;

  // Flatten groups for mobile — show all items directly
  const flatItems: NavItem[] = [];
  navEntries.forEach((entry) => {
    if (isGroup(entry)) {
      flatItems.push(...entry.children);
    } else {
      flatItems.push(entry);
    }
  });

  return (
    <>
      <div className="fixed inset-0 bg-dark-800/40 backdrop-blur-sm z-40 lg:hidden" onClick={onClose} />
      <aside className="fixed left-0 top-0 h-full w-[260px] bg-white dark:bg-dark-800 z-50 shadow-modal lg:hidden animate-slide-up">
        <div className="h-14 flex items-center justify-between px-5 border-b border-surface-border dark:border-dark-500">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Millbank FX" className="w-7 h-7 rounded-lg" />
            <span className="font-display font-bold text-heading-sm dark:text-white">MILLBANK FX</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-secondary dark:hover:bg-dark-600">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <nav className="py-4 px-3 space-y-1">
          {flatItems.map(({ to, icon: Icon, label }) => (
            <NavLink key={to} to={to} onClick={onClose}
              className={({ isActive }) => cn("flex items-center gap-3 px-3 py-2.5 rounded-xl text-body-sm font-medium transition-all duration-150", isActive ? "bg-brand-50 dark:bg-dark-500 text-brand-600 dark:text-accent-300" : "text-dark-400 hover:bg-surface-secondary dark:hover:bg-dark-600 hover:text-dark-600 dark:hover:text-dark-100")}>
              <Icon size={20} className="shrink-0" /><span>{label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>
    </>
  );
}

function TopBar({ onNavigate }: { onNavigate: (path: string) => void }) {
  // Super admins (whoever deploys): safe to deploy right now?
  const superAdmin = getRealUser()?.isSuperAdmin === true;
  return (
    <div className="hidden lg:flex items-center justify-end gap-3 h-14 px-6 border-b border-surface-border/50 dark:border-dark-500/50 bg-white/50 dark:bg-dark-700/50 backdrop-blur-sm sticky top-0 z-30">
      {superAdmin && <DeployStatusPill />}
      <NotificationBell onNavigate={onNavigate} />
    </div>
  );
}

import { ConsentBanner } from "@/components/analytics/ConsentBanner";
import { ImpersonationBanner } from "@/components/layout/ImpersonationBanner";
import { usePageTracking } from "@/hooks/useAnalytics";

interface AppLayoutProps { children: ReactNode; }

export function AppLayout({ children }: AppLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const accountName = useAppStore((s) => s.accountName);
  const logout = useAppStore((s) => s.logout);
  const { entries: navEntries, hub } = useNavEntries();
  const [customising, setCustomising] = useState(false);
  const navigate = useNavigate();
  usePageTracking();

  const queryClient = useQueryClient();
  // Leaving client mode. Clears the selection (which flips the nav back
  // to the Hub) and drops every cached client query so nothing from the
  // previous client can bleed into the next one.
  const handleSwitchAccount = () => { logout(); queryClient.clear(); navigate("/"); };
  const handleLogout = () => { logout(); localStorage.removeItem("mfx_session"); window.location.href = "/"; };

  const { pathname } = useLocation();

  return (
    <div className="min-h-screen bg-surface-secondary dark:bg-dark-700">
      <ImpersonationBanner />
      <div className="hidden lg:block">
        <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(c => !c)} accountName={accountName} onSwitchAccount={handleSwitchAccount} onLogout={handleLogout} navEntries={navEntries}
          onCustomise={hub ? () => setCustomising(true) : undefined} />
      </div>
      {customising && hub && (
        <NavCustomiseModal defaults={hub.defaults} folderIcon={IconFolder}
          current={hub.layout ? { entries: layoutOf(navEntries).entries, hidden: hub.layout.hidden } : layoutOf(hub.defaults)}
          onSave={async (l) => { await hub.save(l); setCustomising(false); }}
          onReset={async () => { await hub.reset(); setCustomising(false); }}
          onClose={() => setCustomising(false)} />
      )}
      <MobileHeader onMenuOpen={() => setMobileOpen(true)} />
      <MobileOverlay open={mobileOpen} onClose={() => setMobileOpen(false)} navEntries={navEntries} />
      <main className={cn("transition-all duration-300 ease-out", "pt-14 lg:pt-0", collapsed ? "lg:pl-[72px]" : "lg:pl-[240px]")}>
        <TopBar onNavigate={navigate} />
        {/* Boards (Tech Tickets) use the full width so every column fits without scrolling. */}
        {HALF_MARGIN_PAGES.some((p) => pathname.startsWith(p)) ? (
          <div className="py-6 lg:py-8" style={{ paddingInline: "max(12px, calc((100% - 1336px) / 4))" }}>{children}</div>
        ) : (
          <div className={cn("mx-auto py-6 lg:py-8", WIDE_PAGES.some((p) => pathname.startsWith(p))
            ? "max-w-none px-4 sm:px-5" : "max-w-[1400px] px-4 sm:px-6 lg:px-8")}>{children}</div>
        )}
      </main>
      <ConsentBanner />
    </div>
  );
}
